<template>
  <div class="new-back fill-height">
		<v-tabs v-model="activeTab" class="api-docs-tabs" background-color="newDesignBackground">
			<v-tab>
				<v-icon left small>mdi-information</v-icon>
				Overview
			</v-tab>
			<v-tab v-for="entity in entities" :key="entity.id">
				<span v-if="entity.googleIcon" class="material-symbols-outlined" style="margin-right: 6px;">{{entity.icon}}</span>
				<v-icon v-else left small>{{ entity.icon }}</v-icon>
				{{ entity.name }}
			</v-tab>
		</v-tabs>

		<v-tabs-items v-model="activeTab">
			<v-tab-item>
				<v-card elevation="2" class="overview-card">
					<v-card-title class="overview-header">
						<v-icon left color="primary">mdi-api</v-icon>
						API Overview
					</v-card-title>
					<v-card-text class="pa-6 new-back">
						<div class="d-flex flex-column gap-6">
							<section>
								<h2 class="text-h6 mb-3">Introduction</h2>
								<p class="text-body-1 mb-2">
									The BlueSurf API provides programmatic access to BlueSurf resources using RESTful endpoints.
									All API requests require authentication using API tokens.
								</p>
								<p class="text-body-1">
									This documentation describes the available endpoints, request formats, and response structures.
								</p>
							</section>

							<section>
								<h2 class="text-h6 mb-3">Base URL</h2>
								<v-card outlined class="code-block pa-3">
									<code class="text-body-2">{{ getBaseUrl() }}</code>
								</v-card>
							</section>

							<section>
								<h2 class="text-h6 mb-3">Authentication</h2>
								<p class="text-body-1 mb-3">
									All API requests must include a valid API token in the Authorization header using Bearer authentication.
								</p>
								<v-card outlined class="code-block pa-3 mb-2">
									<code class="text-body-2">Authorization: Bearer YOUR_API_TOKEN</code>
								</v-card>
								<p class="text-caption grey--text">
									<v-icon x-small class="mr-1">mdi-information</v-icon>
									API tokens can be created and managed in the <router-link to="/apiTokens" class="text-decoration-none">API Tokens</router-link> section.
								</p>
							</section>

							<section>
								<h2 class="text-h6 mb-3">Request Format</h2>
								<p class="text-body-1 mb-2">
									The API accepts JSON-encoded request bodies. All requests should include the following headers:
								</p>
								<v-card outlined class="code-block pa-3">
									<p class="text-body-2 mb-0">
										Content-Type: application/json
										<br>
										Authorization: Bearer YOUR_API_TOKEN
									</p>
								</v-card>
							</section>

							<section>
								<h2 class="text-h6 mb-3">Response Format</h2>
								<p class="text-body-1 mb-2">
									All responses are returned in JSON format. Successful responses use standard HTTP status codes:
								</p>
								<v-simple-table dense class="new-back lighten2">
									<template v-slot:default>
										<thead>
											<tr>
												<th>Status Code</th>
												<th>Description</th>
											</tr>
										</thead>
										<tbody>
											<tr>
												<td><code>200</code></td>
												<td>Success</td>
											</tr>
											<tr>
												<td><code>400</code></td>
												<td>Bad Request - Invalid parameters</td>
											</tr>
											<tr>
												<td><code>401</code></td>
												<td>Unauthorized - Invalid or missing token</td>
											</tr>
											<tr>
												<td><code>403</code></td>
												<td>Forbidden - Insufficient permissions</td>
											</tr>
											<tr>
												<td><code>404</code></td>
												<td>Not Found - Resource doesn't exist</td>
											</tr>
											<tr>
												<td><code>500</code></td>
												<td>Internal Server Error</td>
											</tr>
										</tbody>
									</template>
								</v-simple-table>
							</section>

							<section>
								<h2 class="text-h6 mb-3">Available Entities</h2>
								<v-row>
									<v-col v-for="(entity, index) in entities" :key="entity.id" cols="12" sm="6" md="4" lg="3">
										<v-card outlined class="entity-card new-back lighten2" @click="activeTab = index+1">
											<v-card-text class="d-flex align-center">
												<span v-if="entity.googleIcon" class="material-symbols-outlined primary--text" style="margin-right: 6px;">{{entity.icon}}</span>
												<v-icon v-else :color="entity.color" class="mr-3">{{ entity.icon }}</v-icon>
												<div>
													<div class="font-weight-medium">{{ entity.name }}</div>
													<div class="text-caption grey--text">{{ entity.endpoints.length }} endpoint(s)</div>
												</div>
											</v-card-text>
										</v-card>
									</v-col>
								</v-row>
							</section>
						</div>
					</v-card-text>
				</v-card>
			</v-tab-item>

			<v-tab-item v-for="entity in entities" :key="entity.id">
				<div class="d-flex flex-column">
					<v-card elevation="2">
						<v-card-title class="entity-header">
							
							<span v-if="entity.googleIcon" class="material-symbols-outlined primary--text" style="margin-right: 6px;">{{entity.icon}}</span>
							<v-icon v-else :color="entity.color">{{ entity.icon }}</v-icon>
							<span>{{ entity.name }}</span>
							<v-spacer></v-spacer>
							<v-chip small :color="entity.color" text-color="white">
								{{ entity.endpoints.length }} endpoint(s)
							</v-chip>
						</v-card-title>
					</v-card>

					<v-card v-for="endpoint in entity.endpoints" :key="endpoint.id" elevation="2" class="endpoint-card new-back">
						<v-card-title class="endpoint-header" @click="toggleEndpoint(endpoint.id)">
							<div class="d-flex align-center flex-grow-1">
								<v-chip small :color="getMethodColor(endpoint.method)" text-color="white" class="mr-3 method-chip">
									{{ endpoint.method }}
								</v-chip>
								<code class="text-body-1 endpoint-url">{{ endpoint.url }}</code>
							</div>
							<v-spacer></v-spacer>
							<v-icon>{{ expandedEndpoint[endpoint.id] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
						</v-card-title>

						<v-expand-transition>
							<v-card-text v-show="expandedEndpoint[endpoint.id]" class="pa-4">
								<div class="mb-4">
									<h3 class="text-subtitle-1 font-weight-bold mb-2">Description</h3>
									<p class="text-body-2">{{ endpoint.description }}</p>
								</div>

								<div class="mb-4">
									<h3 class="text-subtitle-1 font-weight-bold mb-2">Authentication</h3>
									<v-alert type="info" dense outlined>
										This endpoint requires a valid API token with appropriate permissions.
									</v-alert>
								</div>

								<div v-if="endpoint.parameters && endpoint.parameters.length > 0" class="mb-4">
									<h3 class="text-subtitle-1 font-weight-bold mb-2">Parameters</h3>
									<v-simple-table dense class="new-back lighten2">
										<template v-slot:default>
											<thead>
												<tr>
													<th>Parameter</th>
													<th>Type</th>
													<th>Required</th>
													<th>Description</th>
												</tr>
											</thead>
											<tbody>
												<tr v-for="param in endpoint.parameters" :key="param.name">
													<td><code>{{ param.name }}</code></td>
													<td><code>{{ param.type }}</code></td>
													<td>
														<v-chip x-small :color="param.required ? 'error' : 'grey'" text-color="white">
															{{ param.required ? 'Yes' : 'No' }}
														</v-chip>
													</td>
													<td>{{ param.description }}</td>
												</tr>
											</tbody>
										</template>
									</v-simple-table>
								</div>

								<div v-if="endpoint.body && endpoint.body.length > 0" class="mb-4">
									<h3 class="text-subtitle-1 font-weight-bold mb-2">Body</h3>
									<v-simple-table dense class="new-back lighten2">
										<template v-slot:default>
											<thead>
												<tr>
													<th>Parameter</th>
													<th>Type</th>
													<th>Required</th>
													<th>Description</th>
												</tr>
											</thead>
											<tbody>
												<tr v-for="param in endpoint.body" :key="param.name">
													<td><code>{{ param.name }}</code></td>
													<td><code>{{ param.type }}</code></td>
													<td>
														<v-chip x-small :color="param.required ? 'error' : 'grey'" text-color="white">
															{{ param.required ? 'Yes' : 'No' }}
														</v-chip>
													</td>
													<td>{{ param.description }}</td>
												</tr>
											</tbody>
										</template>
									</v-simple-table>
								</div>

								<div v-if="endpoint.responseProperties && endpoint.responseProperties.length > 0" class="mb-4">
									<h3 class="text-subtitle-1 font-weight-bold mb-2">Response</h3>
									<div v-if="endpoint.responseDescription" class="text-caption grey--text mb-3">
										{{ endpoint.responseDescription }}
									</div>
									
									<v-simple-table dense class="new-back lighten2">
										<template v-slot:default>
											<thead>
												<tr>
													<th style="width: 40px;"></th>
													<th>Property</th>
													<th>Type</th>
													<th>Description</th>
												</tr>
											</thead>
											<tbody>
												<template v-for="prop in endpoint.responseProperties">
													<tr :class="{ 'expandable-row': prop.expandable }" @click="prop.expandable && toggleResponseProperty(endpoint.id, prop.name)" :key="prop.name">
														<td v-if="prop.expandable" class="text-center" style="cursor: pointer;">
															<v-icon small>{{ expandedResponseProperties[`${endpoint.id}-${prop.name}`] ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
														</td>
														<td v-else></td>
														<td>
															<code>{{ prop.name }}</code>
															<span v-if="prop.expandable" class="text-caption grey--text ml-2">(click to expand)</span>
														</td>
														<td><code>{{ prop.type }}</code></td>
														<td>{{ prop.description }}</td>
													</tr>
													<tr v-if="prop.expandable && expandedResponseProperties[`${endpoint.id}-${prop.name}`] && prop.children" :key="prop.name">
														<td colspan="4" class="pa-0">
															<v-expand-transition>
																<div v-show="expandedResponseProperties[`${endpoint.id}-${prop.name}`]" class="nested-table-container">
																	<v-simple-table dense class="nested-table">
																		<template v-slot:default>
																			<thead>
																				<tr>
																					<th style="padding-left: 40px;">Property</th>
																					<th>Type</th>
																					<th>Description</th>
																				</tr>
																			</thead>
																			<tbody>
																				<tr v-for="childProp in prop.children" :key="childProp.name">
																					<td style="padding-left: 40px;"><code>{{ childProp.name }}</code></td>
																					<td><code>{{ childProp.type }}</code></td>
																					<td>{{ childProp.description }}</td>
																				</tr>
																			</tbody>
																		</template>
																	</v-simple-table>
																</div>
															</v-expand-transition>
														</td>
													</tr>
												</template>
											</tbody>
										</template>
									</v-simple-table>
								</div>
							</v-card-text>
						</v-expand-transition>
					</v-card>
				</div>
			</v-tab-item>
		</v-tabs-items>
  </div>
</template>

<script>
import assignments from '@/json/api-documentation/assignments.json';
import recess from '@/json/api-documentation/recess.json';
import user from '@/json/api-documentation/user.json';
import customer from '@/json/api-documentation/customer.json';
import billable from '@/json/api-documentation/billable.json';

export default {
  title: 'API Documentation',
  data: () => ({
    activeTab: 0,
    expandedEndpoint: {},
    expandedResponseProperties: {},
    entities: [
			assignments,
			recess,
			user,
			customer,
			billable
    ]
  }),
  methods: {
    getBaseUrl() {
      return window.location.origin + '/api';
    },
    toggleEndpoint(endpointId) {
      const isCurrentlyExpanded = this.expandedEndpoint[endpointId];
      
      if (isCurrentlyExpanded) {
        this.$set(this.expandedEndpoint, endpointId, false);
      } else {
        Object.keys(this.expandedEndpoint).forEach(key => {
          this.$set(this.expandedEndpoint, key, false);
        });
        this.$set(this.expandedEndpoint, endpointId, true);
      }
    },
    toggleResponseProperty(endpointId, propertyName) {
      const key = `${endpointId}-${propertyName}`;
      this.$set(this.expandedResponseProperties, key, !this.expandedResponseProperties[key]);
    },
    getMethodColor(method) {
      const colors = {
        GET: 'blue',
        POST: 'green',
        PUT: 'orange',
        PATCH: 'orange',
        DELETE: 'red'
      };
      return colors[method] || 'grey';
    },
    formatJSON(obj) {
      if (typeof obj === 'string') {
        return obj;
      }
      return JSON.stringify(obj, null, 4);
    }
  },
  created() {
    this.entities.forEach(entity => {
      entity.endpoints.forEach(endpoint => {
        this.$set(this.expandedEndpoint, endpoint.id, false);
      });
    });
  }
}
</script>

<style scoped lang="scss">
.api-docs-tabs {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.overview-card {
  .overview-header {
    background-color: rgba(25, 118, 210, 0.1);
    color: var(--v-primary-base);
    font-weight: 600;
    border-bottom: 2px solid var(--v-primary-base);
  }
}

.entity-header {
  background-color: rgba(25, 118, 210, 0.1);
  color: var(--v-primary-base);
  font-weight: 600;
	border-bottom: 2px solid var(--v-primary-base);
}

.endpoint-card {
  border-left: 4px solid rgba(0, 0, 0, 0.12);
  
  .endpoint-header {
    background-color: rgba(0, 0, 0, 0.02);
    cursor: pointer;
    user-select: none;
    transition: background-color 0.2s;
    
    &:hover {
      background-color: rgba(0, 0, 0, 0.05);
    }
  }
  
  .method-chip {
    min-width: 60px;
    font-weight: 600;
    font-size: 0.75rem;
  }
  
  .endpoint-url {
    font-family: 'Courier New', monospace;
    color: var(--v-primary-base);
  }
}

.entity-card {
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
}

.code-block {
	background-color: var(--v-high-back-lighten);
  border: 1px solid rgba(0, 0, 0, 0.12);
  
  code, pre {
    font-family: 'Courier New', 'Consolas', monospace;
    font-size: 0.875rem;
    line-height: 1.5;
  }
  
  pre {
    margin: 0;
    white-space: pre-wrap;
    word-wrap: break-word;
  }
}

::v-deep .v-tabs-items {
  background-color: transparent;
}

::v-deep .v-tab {
  text-transform: none;
  font-weight: 500;
}

.expandable-row {
  cursor: pointer;
  user-select: none;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.02);
  }
}

.nested-table-container {
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 8px 0;
}

.nested-table {
  background-color: var(--v-high-back-lighten);
  border-radius: 4px;
  overflow: hidden;
  width: auto;
  margin: 0 auto;
  
  ::v-deep table {
    width: auto !important;
    margin: 0 auto;
  }
  
  ::v-deep thead {
    background-color: rgba(0, 0, 0, 0.05);
  }
  
  ::v-deep tbody tr {
    background-color: transparent;
    
    &:nth-child(even) {
      background-color: rgba(0, 0, 0, 0.02);
    }
    
    &:hover {
      background-color: rgba(0, 0, 0, 0.05);
    }
  }
}
</style>

